import { useCallback, useMemo, useRef, useState } from "react";
import { GridApiPremium } from "@mui/x-data-grid-premium";
import { Confirm } from "semantic-ui-react";
import {
  AddLink as AddLinkIcon,
  NoteAdd as NoteAddIcon,
  ContentCopy as ContentCopyIcon,
  StorefrontOutlined as StorefrontOutlinedIcon,
} from "@mui/icons-material";
import ServerDataGrid from "../../../components/datagrid/ServerDataGrid";
import { columns } from "./columns";
import ProductContextMenu from "./contextMenu/ProductContextMenu";
import { CreateProductListing } from "../../../components/forms/CreateProductWizard/CreateProductListing";
import { AddCompetitorAsinToProduct } from "../../../components/forms/CreateProductWizard/AddCompetitorAsinToProduct";
import { CreateProductWizard } from "../../../components/forms/CreateProductWizard/CreateProductWizard";
import type { ProductData } from "../../../components/forms/CreateProductWizard/AddSupplyUnitWizard/types";
import type { CreateMenuItem } from "../../../components/datagrid/types";

export function ProductTablePage() {
  const internalApiRef = useRef<GridApiPremium | null>(null);

  const [addAsinProductData, setAddAsinProductData] = useState<{
    productId: number | undefined;
    productTitle: string;
  } | null>(null);

  const [addCompetitorAsinProductData, setAddCompetitorAsinProductData] =
    useState<{
      productId: number | undefined;
      productTitle: string;
    } | null>(null);

  // State for CreateProductWizard
  const [displayProductWizard, setDisplayProductWizard] = useState(false);
  const [initialProduct, setInitialProduct] = useState<ProductData | null>(
    null
  );
  const [productFormMode, setProductFormMode] = useState<
    "create" | "duplicate" | "edit"
  >("create");
  const [productFormPage, setProductFormPage] = useState(1);
  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState(false);

  const handleOpenAddAsin = useCallback(() => {
    const selected = Array.from(
      internalApiRef.current?.getSelectedRows().values() ?? []
    );
    if (selected.length > 1) return;
    if (selected.length === 1) {
      const row = selected[0];
      setAddAsinProductData({
        productId: row.product?.product_id,
        productTitle: row.product?.product_title ?? "",
      });
    } else {
      setAddAsinProductData({ productId: undefined, productTitle: "" });
    }
  }, []);

  const handleCloseAddAsin = useCallback((didCreate?: boolean) => {
    setAddAsinProductData(null);
    if (didCreate) {
      internalApiRef.current?.dataSource?.fetchRows?.();
    }
  }, []);

  const handleOpenAddCompetitorAsin = useCallback(() => {
    const selected = Array.from(
      internalApiRef.current?.getSelectedRows().values() ?? []
    );
    if (selected.length > 1) return;
    if (selected.length === 1) {
      const row = selected[0];
      setAddCompetitorAsinProductData({
        productId: row.product?.product_id,
        productTitle: row.product?.product_title ?? "",
      });
    } else {
      setAddCompetitorAsinProductData({
        productId: undefined,
        productTitle: "",
      });
    }
  }, []);

  const handleCloseAddCompetitorAsin = useCallback((didCreate?: boolean) => {
    setAddCompetitorAsinProductData(null);
    if (didCreate) {
      internalApiRef.current?.dataSource?.fetchRows?.();
    }
  }, []);

  // Transform row data to the shape CreateProductWizard expects.
  const shapeProductDataForWizard = useCallback((row: any): ProductData => {
    return {
      product_id: row.product?.product_id,
      title: row.product?.product_title || "",
      partNumber: row.product?.assembly_part_number || "",
      families:
        row.product_families?.map((subRow: any) => ({
          id: subRow.value,
          title: subRow.label,
        })) || [],
    };
  }, []);

  const handleCreateNew = useCallback(() => {
    setInitialProduct(null);
    setProductFormMode("create");
    setProductFormPage(1);
    setDisplayProductWizard(true);
  }, []);

  const handleDuplicate = useCallback(() => {
    const selected = Array.from(
      internalApiRef.current?.getSelectedRows().values() ?? []
    );
    if (selected.length !== 1) return;
    const productData = shapeProductDataForWizard(selected[0]);
    setInitialProduct(productData);
    setProductFormMode("duplicate");
    setProductFormPage(1);
    setDisplayProductWizard(true);
  }, [shapeProductDataForWizard]);

  const closeCreateForm = useCallback((skipConfirmation?: boolean) => {
    if (skipConfirmation) {
      setDisplayProductWizard(false);
      setInitialProduct(null);
      setProductFormMode("create");
      setProductFormPage(1);
    } else {
      setConfirmCloseModalOpen(true);
    }
  }, []);

  const handleConfirmClose = useCallback(() => {
    setInitialProduct(null);
    setDisplayProductWizard(false);
    setConfirmCloseModalOpen(false);
    setProductFormMode("create");
    setProductFormPage(1);
  }, []);

  const handleConfirmCloseCancel = useCallback(() => {
    setConfirmCloseModalOpen(false);
  }, []);

  const createMenuItems = useMemo<CreateMenuItem[]>(
    () => [
      {
        label: "Create New Product",
        icon: <NoteAddIcon fontSize="small" />,
        onClick: handleCreateNew,
      },
      {
        label: "Create Related Product",
        icon: <ContentCopyIcon fontSize="small" />,
        onClick: handleDuplicate,
        requiresRowSelection: true,
        disabledReason: "Select 1 row",
      },
      {
        label: "Create Product Listing",
        icon: <AddLinkIcon fontSize="small" />,
        onClick: handleOpenAddAsin,
        disabledWhenMultipleSelected: true,
        disabledReason: "Select at most 1 row",
      },
      {
        label: "Add Competitor ASIN to Product",
        icon: <StorefrontOutlinedIcon fontSize="small" />,
        onClick: handleOpenAddCompetitorAsin,
        disabledWhenMultipleSelected: true,
        disabledReason: "Select at most 1 row",
      },
    ],
    [
      handleCreateNew,
      handleDuplicate,
      handleOpenAddAsin,
      handleOpenAddCompetitorAsin,
    ]
  );

  return (
    <>
      <Confirm
        open={confirmCloseModalOpen}
        content="Are you sure you want to close the create form? Any unsaved changes will be lost."
        onCancel={handleConfirmCloseCancel}
        onConfirm={handleConfirmClose}
      />

      {displayProductWizard && (
        <CreateProductWizard
          onClose={closeCreateForm}
          initialProduct={initialProduct}
          initialPage={productFormPage}
          mode={productFormMode}
        />
      )}

      {addAsinProductData && (
        <CreateProductListing
          productId={addAsinProductData.productId}
          productTitle={addAsinProductData.productTitle}
          onClose={handleCloseAddAsin}
        />
      )}

      {addCompetitorAsinProductData && (
        <AddCompetitorAsinToProduct
          productId={addCompetitorAsinProductData.productId}
          productTitle={addCompetitorAsinProductData.productTitle}
          onClose={handleCloseAddCompetitorAsin}
        />
      )}

      <ServerDataGrid
        columns={columns}
        apiEndpoint="/datagrid/product"
        getRowId={(row) => row.product.product_id}
        getDeleteBlockReason={(row) =>
          row.product_asin_list_view?.asin_list
            ? "Product has associated Amazon listings"
            : undefined
        }
        ContextMenuComponent={ProductContextMenu}
        tableName="product"
        enableSearch={true}
        pageSizeOptions={[25, 50, 100, 250, 500]}
        height={"calc(100vh - 52.6px)"}
        initialStateOverride={{
          sorting: {
            sortModel: [{ field: "product.created_at", sort: "desc" }],
          },
        }}
        enableCreate={true}
        createMenuItems={createMenuItems}
        apiRef={internalApiRef}
      />
    </>
  );
}
